import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { isFormType } from '../../../api/forms';
import { usePublished, useSaveDraft, useTemplateVersions, useTemplates, type TemplateVersion } from '../../../api/templates';
import { useEnumLabel } from '../../../api/enums';
import { isApiError } from '../../../api/errors';
import { Breadcrumbs, Button, ButtonLink, ErrorSummary, SummaryList, Table, Tag } from '../../../components/idsk';
import { useNavigateWithToast } from '../../../components/common/navigateWithToast';
import { PageHeading } from '../../../layout/PageHeading';
import { usePageTitle } from '../../../layout/usePageTitle';
import { formatDateTime } from '../../../lib/format';
import { NotFoundPage } from '../../common/NotFoundPage';
import { Loading } from '../../common/LoadingPage';
import { RequireAdmin } from '../../common/RequireAdmin';
import { ExportTemplateButton } from './TemplateExport';

export function TemplateDetailPage() {
  return (
    <RequireAdmin>
      <TemplateDetail />
    </RequireAdmin>
  );
}

export function StatusTag({ status }: { status: TemplateVersion['status'] }) {
  const { t } = useTranslation();
  return <Tag colour={status === 'PUBLISHED' ? 'green' : status === 'DRAFT' ? 'blue' : 'grey'}>{t(`templates.status.${status}`)}</Tag>;
}

function TemplateDetail() {
  const { type } = useParams();
  const { t } = useTranslation();
  const label = useEnumLabel();
  const formType = isFormType(type) ? type : undefined;
  const templates = useTemplates();
  const versions = useTemplateVersions(formType);
  const published = usePublished(formType);
  const saveDraft = useSaveDraft(formType ?? 'PROPOSAL');
  const navigateWithToast = useNavigateWithToast();
  const [error, setError] = useState<string | null>(null);
  const name = formType ? label('formTypes', formType) : '';
  usePageTitle([name, t('templates.title')]);
  if (!formType) return <NotFoundPage />;
  const summary = templates.data?.find((x) => x.formType === formType);

  const createDraft = async () => {
    if (!published.data?.definition) return;
    try {
      await saveDraft.mutateAsync({ definition: published.data.definition });
      navigateWithToast(`/sablony/${formType}/koncept`, t('templates.draftCreated'));
    } catch (e) {
      setError(isApiError(e) ? e.message : t('errors.unexpected'));
    }
  };

  return (
    <>
      <Breadcrumbs items={[{ text: t('nav.home'), to: '/' }, { text: t('templates.title'), to: '/sablony' }, { text: name }]} />
      {error && <ErrorSummary errors={[{ text: error }]} />}
      <PageHeading caption={t('templates.caption1')}>{name}</PageHeading>
      {summary && (
        <SummaryList
          rows={[
            { key: t('templates.published'), value: summary.published ? t('templates.versionPublished', { n: summary.published.versionNo, when: formatDateTime(summary.published.publishedAt), who: summary.published.publishedBy }) : '–' },
            {
              key: t('templates.draft'),
              value: summary.draft ? t('templates.draftInfo', { n: summary.draft.versionNo, when: formatDateTime(summary.draft.lastModifiedAt), who: summary.draft.lastModifiedBy }) : t('templates.noDraft'),
            },
          ]}
        />
      )}
      <div className="idsk-button-group">
        {summary?.draft ? (
          <ButtonLink to={`/sablony/${formType}/koncept`}>{t('templates.editDraft')}</ButtonLink>
        ) : (
          <Button onClick={() => void createDraft()} preventDoubleClick>
            {t('templates.createDraft')}
          </Button>
        )}
        {summary?.published && (
          <ButtonLink to={`/sablony/${formType}/verzie/${summary.published.versionNo}`} variant="secondary">
            {t('templates.viewPublished')}
          </ButtonLink>
        )}
        <ButtonLink to={`/sablony/${formType}/import`} variant="secondary">
          {t('templates.import.open')}
        </ButtonLink>
      </div>
      <h2 className="govuk-heading-m">{t('templates.history')}</h2>
      {versions.isPending ? (
        <Loading />
      ) : (
        <Table<TemplateVersion>
          caption={t('templates.historyCaption', { name })}
          captionClassName="govuk-visually-hidden"
          rows={versions.data ?? []}
          rowKey={(v) => String(v.versionNo)}
          columns={[
            {
              key: 'no',
              header: t('templates.version'),
              rowHeader: true,
              render: (v) =>
                v.status === 'DRAFT' ? (
                  <Link className="govuk-link" to={`/sablony/${formType}/koncept`}>
                    {t('templates.versionN', { n: v.versionNo })}
                  </Link>
                ) : (
                  <Link className="govuk-link" to={`/sablony/${formType}/verzie/${v.versionNo}`}>
                    {t('templates.versionN', { n: v.versionNo })}
                  </Link>
                ),
            },
            { key: 'status', header: t('templates.state'), render: (v) => <StatusTag status={v.status} /> },
            { key: 'publishedAt', header: t('templates.publishedAt'), render: (v) => (v.publishedAt ? formatDateTime(v.publishedAt) : '–') },
            { key: 'publishedBy', header: t('templates.publishedBy'), render: (v) => v.publishedBy ?? '–' },
            { key: 'file', header: t('templates.export.column'), render: (v) => <ExportTemplateButton type={formType} versionNo={v.versionNo} name={name} variant="link" /> },
          ]}
        />
      )}
    </>
  );
}
